<template>
  <div class="address-select">

    <!-- IČO input + akce -->
    <div v-if="!useExternalAddresses" class="d-flex gap-2 align-items-end mb-1">
      <div class="flex-grow-1">
        <label class="form-label mb-1">IČO</label>
        <input
            v-model.trim="search"
            type="text"
            inputmode="numeric"
            class="form-control"
            placeholder="Zadejte IČO…"
            @keyup.enter="onSearchConfirm"
        />
      </div>

      <button
          class="btn btn-primary text-white"
          :disabled="loading || !search"
          @click="onSearchConfirm"
      >
        <span class="spinner-border spinner-border-sm me-2" v-if="loading" aria-hidden="true"></span>
        Vyhledat
      </button>

      <button
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="reset"
      >
        Reset
      </button>
    </div>
    <small v-if="!useExternalAddresses" class="text-muted d-block mb-3">Zadej IČO a potvrď Enterem nebo tlačítkem.</small>

    <!-- Stav: chyba / prázdno -->
    <div v-if="error" class="alert alert-danger">
      Nepodařilo se načíst adresy. Zkus to prosím znovu.
    </div>

    <div v-if="!loading && searched && addresses.length === 0" class="alert alert-warning">
      {{ noResultsText }}
    </div>

    <!-- Filtr nad nalezenými adresami (zobrazí se až po vyhledání) -->
    <div v-if="searched && addresses.length" class="input-group mb-3">
      <span class="input-group-text"><i class="bi bi-search"></i></span>
      <input
          v-model.trim="filterNazevd"
          type="text"
          class="form-control"
          placeholder="Filtrovat podle názvu provozovny…"
      />
      <button v-if="filterNazevd" class="btn btn-outline-secondary" @click="filterNazevd = ''">
        <i class="bi bi-x"></i>
      </button>
    </div>
    <div v-if="searched && addresses.length && filteredAddresses.length === 0" class="alert alert-info">
      Žádná provozovna neodpovídá zadanému filtru.
    </div>
    <!-- Tabulka -->
    <div v-if="addresses.length" class="table-responsive">
      <table class="table table-sm align-middle mb-3">
        <thead>
        <tr>
          <th class="text-start" style="width: 42%">Provozovna</th>
          <th class="text-start" style="width: 38%">Adresa</th>
          <th style="width: 20%"></th>
        </tr>
        </thead>

        <tbody>
        <template v-for="group in groupedAddresses" :key="group.companyKey">
          <!-- group header -->
          <tr class="address-group-header">
            <td colspan="3" class="text-start fw-semibold">
              {{ group.companyLabel }}
              <span class="text-muted fw-normal ms-2">({{ group.items.length }})</span>
            </td>
          </tr>

          <!-- items -->
          <tr v-for="addr in group.items" :key="addr.id" :class="selectedAddress === addr.id ? 'table-success' : ''">
            <td class="text-start">
              <div v-if="addr.custom?.nazevd">{{ addr.custom.nazevd }}</div>
              <div v-else class="text-muted fst-italic">–</div>
            </td>

            <td class="text-start">
              <div>{{ addr.street || '' }}</div>
              <div class="text-muted small">{{ addr.zip || '' }} {{ addr.city || '' }}</div>
              <div v-if="addr.ico" class="text-muted small">IČO: {{ addr.ico }}</div>
              <div v-if="addr.custom?.icod && addr.custom.icod !== addr.ico" class="text-muted small">IČO dod.: {{ addr.custom.icod }}</div>
            </td>

            <td class="text-end">
              <button
                  class="btn btn-sm"
                  :class="selectedAddress === addr.id ? 'btn-success' : 'btn-outline-primary'"
                  @click="selectAddress(addr)"
              >
                <i class="bi me-1" :class="selectedAddress === addr.id ? 'bi-check-lg' : 'bi-hand-index'"></i>{{ selectedAddress === addr.id ? 'Vybráno' : 'Vybrat' }}
              </button>
            </td>
          </tr>
        </template>
        </tbody>
      </table>
    </div>

    <!-- potvrzení výběru -->
    <div class="text-start mt-2">
      <button
          class="btn btn-primary text-white"
          :disabled="creating || externalLoading || !selectedAddress"
          @click="setAddress"
      >
        <span class="spinner-grow spinner-grow-sm me-2" v-if="creating || externalLoading" aria-hidden="true"></span>
        {{ confirmButtonText }}
      </button>

      <div class="text-muted small mt-1" v-if="selectedLabel">
        Vybráno: {{ selectedLabel }}
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import type { Address, ResponseEntityAddress } from "@haxo-cz/orders-ts-client";
import { useAppStore } from "@/stores/appStore";

type AddressGroup = {
  companyKey: string;
  companyLabel: string;
  items: ResponseEntityAddress[];
};

export default {
  name: "AddressSelect",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    useExternalAddresses: {
      type: Boolean,
      default: false,
    },
    externalAddresses: {
      type: Array,
      default: () => [],
    },
    confirmButtonText: {
      type: String,
      default: "Potvrdit vybranou adresu",
    },
    externalLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "confirm"],
  data() {
    return {
      appStore: useAppStore(),
      filterNazevd: "",
      // data
      addresses: [] as ResponseEntityAddress[],

      // search
      search: "",
      page: 1,
      limit: 200, // u tabulky dává větší smysl natáhnout víc najednou
      hasMore: true,

      // ui state
      loading: false,
      creating: false,
      error: false,
      searched: false,

      // selection
      selectedLabel: "",
      selectedAddress: this.modelValue || null as string | null,
    };
  },

  computed: {
    noResultsText(): string {
      if (this.useExternalAddresses) {
        return "Pro zadané IČO nebyly nalezeny žádné provozovny.";
      }

      return "Pro zadané IČO nebyly nalezeny žádné adresy.";
    },
    filteredAddresses(): ResponseEntityAddress[] {
      const q = (this.filterNazevd || "").trim().toLowerCase();
      if (!q) return this.addresses;

      return this.addresses.filter(a => {
        const nazevd = (a.custom?.nazevd || "").toString().toLowerCase();
        return nazevd.includes(q);
      });
    },

    groupedAddresses(): AddressGroup[] {
      const map = new Map<string, ResponseEntityAddress[]>();

      for (const a of this.filteredAddresses) {
        const key = (a.company || "Neznámá firma").trim().toLowerCase();
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(a);
      }

      const groups: AddressGroup[] = Array.from(map.entries())
          .sort(([ak], [bk]) => ak.localeCompare(bk, "cs"))
          .map(([companyKey, items]) => {
            const label = items[0]?.company || "Neznámá firma";
            items.sort((x, y) => (x.custom?.nazevd || "").localeCompare((y.custom?.nazevd || ""), "cs"));
            return { companyKey, companyLabel: label, items };
          });

      return groups;
    },
  },
  watch: {
    modelValue(newValue: string) {
      this.selectedAddress = newValue || null;
      this.updateSelectedLabel();
    },
    externalAddresses: {
      handler() {
        if (this.useExternalAddresses) {
          this.applyExternalAddresses();
        }
      },
      deep: true,
    },
    useExternalAddresses(newValue: boolean) {
      if (newValue) {
        this.applyExternalAddresses();
      }
    },
  },
  mounted() {
    if (this.useExternalAddresses) {
      this.applyExternalAddresses();
    } else if (this.modelValue) {
      this.selectedAddress = this.modelValue;
      this.updateSelectedLabel();
    }
  },
  methods: {
    applyExternalAddresses() {
      const providedAddresses = Array.isArray(this.externalAddresses)
          ? (this.externalAddresses as ResponseEntityAddress[])
          : [];

      this.addresses = [...providedAddresses];
      this.loading = false;
      this.error = false;
      this.searched = true;
      this.page = 1;
      this.hasMore = false;

      const preferredAddressId = this.modelValue || this.selectedAddress;
      const addressExists = preferredAddressId
          ? this.addresses.some((address) => address.id === preferredAddressId)
          : false;

      if (addressExists) {
        this.selectedAddress = preferredAddressId as string;
      } else {
        this.selectedAddress = null;
        this.$emit("update:modelValue", "");
      }

      this.updateSelectedLabel();
    },
    updateSelectedLabel() {
      if (!this.selectedAddress) {
        this.selectedLabel = "";
        return;
      }

      const selectedAddress = this.addresses.find((address) => address.id === this.selectedAddress);
      this.selectedLabel = selectedAddress ? this.formatAddress(selectedAddress) : "";
    },
    reset() {
      this.filterNazevd = "";
      this.addresses = [];
      this.page = 1;
      this.hasMore = true;
      this.loading = false;
      this.error = false;
      this.searched = false;
      this.selectedAddress = null;
      this.selectedLabel = "";
    },

    async onSearchConfirm() {
      if (this.useExternalAddresses) return;
      this.reset();
      if (!this.search) return;
      await this.fetchAllAddressesForIco();
    },

    // tabulka dává smysl stáhnout vše (nebo do rozumného limitu) a pak groupovat
    async fetchAllAddressesForIco() {
      if (!this.search || this.loading) return;

      this.loading = true;
      this.error = false;

      try {
        if (!this.appStore.selectedProject) {
          throw new Error("Selected project is not available");
        }
        this.searched = true;

        // stáhneme stránkovaně, dokud jsou data
        while (this.hasMore) {
          const response = await this.$addressesApi.findAllAddresses(
              this.appStore.selectedProject.spaceId,
              this.appStore.selectedProject._id,
              this.page,
              this.limit,
              this.search
          );

          this.addresses.push(...response.data.data);
          this.hasMore = this.addresses.length < response.data.meta.totalCount;
          this.page++;
        }
      } catch (e) {
        console.error("Chyba při načítání adres:", e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    selectAddress(address: ResponseEntityAddress) {
      this.$emit("update:modelValue", address.id);
      this.selectedLabel = this.formatAddress(address);
      this.selectedAddress = address.id;
    },

    formatAddress(address: Address) {
      return `${address.company} - ${address.country}, ${address.street || ""}, ${address.city || ""}, ${address.zip || ""} | ${address.custom?.nazevd || ""}`;
    },

    async setAddress() {
      if (!this.selectedAddress) return;
      if (this.useExternalAddresses) {
        this.$emit("confirm", this.selectedAddress);
        return;
      }
      if (!this.appStore.customer) return;

      this.creating = true;
      try {
        const response = await this.$customersApi.updateCustomer(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.appStore.customer.id,
            {
              addresses: {
                billing: this.selectedAddress,
                shipping: this.selectedAddress,
              },
            }
        );

        this.appStore.customer = response.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.address-select {
  margin-bottom: 1rem;
}
.address-group-header td {
  background-color: #e9ecef !important;
}
</style>
